import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { brand } from '../theme';
import { gsap, useGsap, QUERIES, countUp, reveal } from '../animation/gsap';
import SectionHeading from '../components/SectionHeading';

/**
 * Why people call the company back.
 *
 * The copy is carried over word for word from the PHP theme's
 * template-parts/section-why-us.php — it is the owner's own wording and it is
 * the most persuasive thing on the page, so it is not paraphrased.
 */

const REASONS = [
	{
		title: 'The owner is on the job',
		body: 'Mirjan Pelaj runs the company and works on the tools. The person who gives you the price is the person who does the work, so nothing gets lost between the office and your home.',
	},
	{
		title: 'We prepare before we finish',
		body: 'Tile and flooring only last when what sits underneath is right. We level floors, waterproof wet areas, and fix the subfloor first, even when nobody will ever see that part.',
	},
	{
		title: 'We leave your home clean',
		body: 'We cover floors, mask edges, and clean up at the end of each day. You keep living in your home while we work, so we treat it that way.',
	},
	{
		title: 'A clear price, in writing',
		body: 'Your estimate is free and it explains what is included. If something changes once we open a wall, we tell you before we carry on.',
	},
];

/*
 * Deliberately promises, not statistics. Earlier drafts counted things ("7
 * trades", "6+ communities") that read as claims someone could check — and the
 * owner would rather state the standard than defend a number. Three hundreds:
 * simple, confident, and nothing to verify.
 */
const STATS = [
	{ value: 100, suffix: '%', label: 'Quality workmanship' },
	{ value: 100, suffix: '%', label: 'Free, no-pressure estimates' },
	{ value: 100, suffix: '%', label: 'Committed to your satisfaction' },
];

export default function WhyUs() {
	const root = useGsap(({ mm }) => {
		const q = gsap.utils.selector(root);

		mm.add(QUERIES.motion, () => {
			reveal(q('[data-reason]'), {
				trigger: q('[data-reasons]')[0],
				start: 'top 78%',
				y: 40,
				duration: 0.7,
				stagger: 0.12,
				ease: 'power3.out',
			});

			// The numbers run up from zero as the strip arrives.
			q('[data-stat-value]').forEach((element) => {
				countUp(element, Number(element.dataset.value), element.dataset.suffix ?? '');
			});

			reveal(q('[data-stat]'), {
				trigger: q('[data-stats]')[0],
				start: 'top 85%',
				y: 24,
				stagger: 0.1,
			});
		});
	}, []);

	return (
		<Box ref={root} component="section" sx={{ py: { xs: 8, lg: 14 }, backgroundColor: brand.white }}>
			<Container>
				<SectionHeading
					eyebrow="Why us"
					title="Why People Call Us Back"
					lead="Reliable work, honest pricing, and a finish that lasts — here is what you can expect on every job."
				/>

				<Box
					data-reasons
					sx={{
						mt: 7,
						display: 'grid',
						gap: 4,
						gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
					}}
				>
					{REASONS.map((reason, index) => (
						<Box
							key={reason.title}
							data-reason
							sx={{
								position: 'relative',
								pl: 4,
								borderLeft: `2px solid ${brand.greyLight}`,
								transition: 'border-color 300ms ease',
								'&:hover': { borderColor: brand.accent },
							}}
						>
							<Typography
								aria-hidden="true"
								sx={{
									position: 'absolute',
									left: '-1.1rem',
									top: 0,
									width: '2rem',
									height: '2rem',
									borderRadius: '50%',
									display: 'grid',
									placeItems: 'center',
									backgroundColor: brand.accent,
									color: brand.white,
									fontSize: '0.8rem',
									fontWeight: 700,
								}}
							>
								{index + 1}
							</Typography>

							<Typography variant="h4" component="h3" sx={{ mb: 1.5 }}>
								{reason.title}
							</Typography>
							<Typography sx={{ color: brand.textMuted }}>{reason.body}</Typography>
						</Box>
					))}
				</Box>

				<Box
					data-stats
					sx={{
						mt: 10,
						py: 5,
						px: { xs: 3, md: 6 },
						borderRadius: 2,
						backgroundColor: brand.dark,
						color: brand.textInvert,
						display: 'grid',
						gap: 4,
						gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
						textAlign: 'center',
					}}
				>
					{STATS.map((stat) => (
						<Box key={stat.label} data-stat>
							<Typography
								data-stat-value
								data-value={stat.value}
								data-suffix={stat.suffix}
								variant="h2"
								component="p"
								sx={{ color: brand.accent, mb: 1 }}
							>
								{/* The starting text matters: with JavaScript off, or reduced
								    motion on, this is what stays on screen. */}
								{stat.value}
								{stat.suffix}
							</Typography>
							<Typography sx={{ color: brand.textInvertMuted, fontSize: '0.95rem' }}>
								{stat.label}
							</Typography>
						</Box>
					))}
				</Box>
			</Container>
		</Box>
	);
}
